import type { ComponentProps, ReactNode } from "react";
import { Link } from "react-router";

type Variant = "primary" | "gradient" | "outline" | "outline-dark" | "white" | "ghost";
type Size = "sm" | "md" | "lg";

interface CtaButtonBaseProps {
	variant?: Variant;
	size?: Size;
	startContent?: ReactNode;
	endContent?: ReactNode;
	className?: string;
	children: ReactNode;
}

const VARIANT_CLASS: Record<Variant, string> = {
	primary:
		"bg-accent text-accent-foreground shadow-md shadow-accent/20 hover:bg-accent/90 hover:shadow-lg hover:shadow-accent/30 active:bg-accent/80",
	gradient: "landing-bg-brand-gradient text-white shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 active:opacity-95",
	outline: "border border-divider bg-content1/80 text-foreground backdrop-blur hover:border-foreground/30 hover:bg-content2",
	"outline-dark": "border border-white/25 bg-white/5 text-white backdrop-blur hover:border-white/45 hover:bg-white/10",
	white: "bg-white text-accent shadow-md hover:bg-white/90",
	ghost: "border border-white/40 bg-transparent text-white hover:bg-white/10",
};

const SIZE_CLASS: Record<Size, string> = {
	sm: "h-9 px-4 text-sm",
	md: "h-11 px-5 text-sm",
	lg: "h-12 px-7 text-base",
};

const BASE =
	"inline-flex items-center justify-center gap-2 rounded-full font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60";

interface CtaLinkProps extends CtaButtonBaseProps, Omit<ComponentProps<typeof Link>, "className" | "children"> {}

export function CtaLink({ variant = "primary", size = "md", startContent, endContent, className = "", children, ...rest }: CtaLinkProps) {
	return (
		<Link className={`${BASE} ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${className}`} {...rest}>
			{startContent}
			{children}
			{endContent}
		</Link>
	);
}

interface CtaAnchorProps extends CtaButtonBaseProps, Omit<ComponentProps<"a">, "className" | "children"> {}

export function CtaAnchor({
	variant = "primary",
	size = "md",
	startContent,
	endContent,
	className = "",
	children,
	...rest
}: CtaAnchorProps) {
	return (
		<a className={`${BASE} ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${className}`} {...rest}>
			{startContent}
			{children}
			{endContent}
		</a>
	);
}

interface CtaButtonProps extends CtaButtonBaseProps, Omit<ComponentProps<"button">, "className" | "children"> {
	isLoading?: boolean;
}

export function CtaButton({
	variant = "primary",
	size = "md",
	startContent,
	endContent,
	className = "",
	children,
	isLoading,
	disabled,
	...rest
}: CtaButtonProps) {
	return (
		<button
			type="button"
			className={`${BASE} ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${className}`}
			disabled={disabled || isLoading}
			{...rest}
		>
			{!isLoading && startContent}
			{isLoading && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />}
			{children}
			{!isLoading && endContent}
		</button>
	);
}
